import {
  Alert,
  AlertTitle,
  Box,
  CircularProgress,
  Collapse,
  Grid,
  Paper,
  Slide,
  StyledEngineProvider,
  ThemeProvider,
  Typography,
} from '@mui/material';
import React, { HTMLAttributes, useContext, useEffect, useState } from 'react';
import { BaseTheme } from '../../../BeaconTheme';
import { IQuickNotation } from '../../../pages/Settings/NotationWidget/Interfaces/IQuickNotation';
import { NotationWidgetService } from '../../../pages/Settings/NotationWidget/Services/NotationWidgetService';
import { NotationType } from '../../../shared-types/Notation/NotationType';
import { hasFeature } from '../../contexts/InstitutionContext';
import NotationAttributeContext from '../../contexts/NotationAttributesContext';
import UserContext from '../../contexts/UserContext';
import ErrorBox from '../../ErrorBox';
import AlertIcon from '../../Icons/AlertIcon';
import EncouragementIcon from '../../Icons/EncouragementIcon';
import UpdateIcon from '../../Icons/UpdateIcon';
import AlertNotationForm from './AlertNotationForm';
import EncouragementNotationForm from './EncouragementNotationForm';
import NotationSelection from './NotationSelection';
import UpdateNotationForm from './UpdateNotationForm';

const UpdateNotation = (props: HTMLAttributes<HTMLDivElement>) => {
  return (
    <NotationSelection
      name="Update"
      description="Updates allow you to share information about a student with members of their success network."
      icon={<UpdateIcon />}
      {...props}
    />
  );
};

const AlertNotation = (props: HTMLAttributes<HTMLDivElement>) => {
  return (
    <NotationSelection
      name="Alert"
      description="Alerts are very important updates and will require a response from the Success Network."
      icon={<AlertIcon />}
      {...props}
    />
  );
};

const EncouragementNotation = (props: HTMLAttributes<HTMLDivElement>) => {
  return (
    <NotationSelection
      name="Encouragement"
      description=" Encouragements help students adopt a growth mindset and reorient their attitudes toward learning."
      icon={<EncouragementIcon />}
      {...props}
    />
  );
};

interface INotationSelection {
  isPreview?: boolean;
  setStage: (x: NotationType) => void;
  showAlert: boolean;
  showUpdate: boolean;
  showEncouragement: boolean;
}

const MainNotationSelection = (props: INotationSelection) => {
  const goToUpdate = () => {
    if (props.isPreview) return;
    props.setStage(NotationType.Update);
  };
  const goToAlert = () => {
    if (props.isPreview) return;
    props.setStage(NotationType.Alert);
  };
  const goToEncouragement = () => {
    if (props.isPreview) return;
    props.setStage(NotationType.Encouragement);
  };
  return (
    <Grid container direction="column">
      <Grid item style={{ marginBottom: '1em' }}>
        <header>
          <Typography variant="h4" align="center">
            Pick Notation Type
          </Typography>
        </header>
      </Grid>
      {props.showUpdate && (
        <Grid item>
          <UpdateNotation
            onClick={goToUpdate}
            onKeyPress={event => {
              if (event.key === 'Enter') goToUpdate();
            }}
            role="button"
          />
        </Grid>
      )}
      {props.showAlert && (
        <Grid item>
          <AlertNotation
            onClick={goToAlert}
            onKeyPress={event => {
              if (event.key === 'Enter') goToAlert();
            }}
            role="button"
          />
        </Grid>
      )}
      {props.showEncouragement && (
        <Grid item>
          <EncouragementNotation
            onClick={goToEncouragement}
            onKeyPress={event => {
              if (event.key === 'Enter') goToEncouragement();
            }}
            role="button"
          />
        </Grid>
      )}
    </Grid>
  );
};

export interface INotationWidgetSelection {
  isPreview?: boolean;
  showAlert?: boolean;
  showUpdate?: boolean;
  showEncouragement?: boolean;
}

const NotationWidgetSelection = (props: INotationWidgetSelection) => {
  const user = useContext(UserContext);
  const { encouragementTypes } = useContext(NotationAttributeContext);

  const defaultPermissions = {
    titleText: '',
    description: '',
    updateShowPermission: 0b1111,
    updateAudiencePermission: 0b1110,
    updateAddAudiencePermission: 0b1110,
    updateOccurancePermission: 0b1110,
    updateCategoryPermission: 0b1000,
    updateReasonPermission: 0b1000,
    alertShowPermission: 0b1111,
    alertAudiencePermission: 0b1110,
    alertAddAudiencePermission: 0b1110,
    alertAssignToPermission: 0b1100,
    alertOccuranceDatePermission: 0b1110,
    alertCategoryPermission: 0b1000,
    alertReasonPermission: 0b1000,
    encouragementShowPermission: hasFeature('Encouragements') ? 0b1110 : 0b0000,
    encouragementAudiencePermission: 0b0000,
    height: 450,
    width: 350,
  } as IQuickNotation;

  const [quickWidgetPermissions, setQuickWidgetPermissions] =
    useState<IQuickNotation>({} as IQuickNotation);
  const [encouragementTypeExists, setEncouragementTypeExists] =
    useState<boolean>(false);

  const [selectedNotationType, setSelectedNotationType] =
    useState<NotationType | null>();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isErrored, setIsErrored] = useState<boolean>(false);
  const [showSuccess, setShowSuccess] = useState<boolean>(false);

  useEffect(() => {
    if (props.isPreview) {
      setIsLoading(false);
      return;
    }

    NotationWidgetService.GetQuickNotationWidgetAllowAnonymous()
      .then(p => {
        if (p) setQuickWidgetPermissions(p);
        else setQuickWidgetPermissions(defaultPermissions);
      })
      .catch(e => setIsErrored(true))
      .finally(() => setTimeout(setIsLoading, 500, false));

    setEncouragementTypeExists(encouragementTypes.length !== 0);
  }, []);

  useEffect(() => {
    setShowSuccess(false);
  }, [selectedNotationType]);

  let userRole = 0;
  if (user.isStudent) {
    userRole = 0b0001;
  }
  if (user.isPeerAdvisor) {
    userRole = 0b0010;
  }
  if (user.isAdvisor) {
    userRole = 0b0100;
  }
  if (
    user.isGeneralist ||
    user.isExecutive ||
    user.isAdministrator ||
    user.isSupport
  ) {
    userRole = 0b1000;
  }

  const updatePermissions = {
    updateAddAudiencePermission:
      (quickWidgetPermissions.updateAddAudiencePermission & userRole) !== 0,
    updateAudiencePermission:
      (quickWidgetPermissions.updateAudiencePermission & userRole) !== 0,
    updateCategoryPermission:
      (quickWidgetPermissions.updateCategoryPermission & userRole) !== 0,
    updateOccurancePermission:
      (quickWidgetPermissions.updateOccurancePermission & userRole) !== 0,
    updateReasonPermission:
      (quickWidgetPermissions.updateReasonPermission & userRole) !== 0,
    updateShowPermission:
      (quickWidgetPermissions.updateShowPermission & userRole) !== 0,
  };
  const alertPermissions = {
    alertAddAudiencePermission:
      (quickWidgetPermissions.alertAddAudiencePermission & userRole) !== 0,
    alertAssignToPermission:
      (quickWidgetPermissions.alertAssignToPermission & userRole) !== 0,
    alertAudiencePermission:
      (quickWidgetPermissions.alertAudiencePermission & userRole) !== 0,
    alertCategoryPermission:
      (quickWidgetPermissions.alertCategoryPermission & userRole) !== 0,
    alertOccuranceDatePermission:
      (quickWidgetPermissions.alertOccuranceDatePermission & userRole) !== 0,
    alertReasonPermission:
      (quickWidgetPermissions.alertReasonPermission & userRole) !== 0,
    alertShowPermission:
      (quickWidgetPermissions.alertShowPermission & userRole) !== 0,
  };
  const encouragementPermissions = {
    encouragementAudiencePermission:
      (quickWidgetPermissions.encouragementAudiencePermission & userRole) !== 0,
    encouragementShowPermission:
      (quickWidgetPermissions.encouragementShowPermission & userRole) !== 0 &&
      hasFeature('Encouragements') &&
      encouragementTypeExists,
  };

  if (props.showAlert !== undefined) {
    alertPermissions.alertShowPermission = props.showAlert;
  }
  if (props.showUpdate !== undefined) {
    updatePermissions.updateShowPermission = props.showUpdate;
  }
  if (props.showEncouragement !== undefined) {
    encouragementPermissions.encouragementShowPermission =
      props.showEncouragement;
  }

  const hasPermissionToCreateNotation =
    updatePermissions.updateShowPermission ||
    alertPermissions.alertShowPermission ||
    encouragementPermissions.encouragementShowPermission;

  const SubmitConfirmation = () => {
    return (
      <Collapse in={showSuccess}>
        <Alert variant="filled" severity="success">
          <AlertTitle>Success</AlertTitle>
          Notation Created
        </Alert>
      </Collapse>
    );
  };

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={BaseTheme}>
        <Paper elevation={2}>
          <Box
            overflow="auto"
            style={{
              backgroundColor: 'white',
              padding: '10px',
              margin: '1em auto',
            }}
          >
            {isErrored && (
              <ErrorBox
                minHeight={200}
                alignContent="center"
                alignItems="center"
                justifyContent="center"
                display="flex"
                showCorporateContact
              />
            )}
            {isLoading && !isErrored && (
              <Box
                height={200}
                alignContent="center"
                alignItems="center"
                justifyContent="center"
                display="flex"
              >
                <CircularProgress />
              </Box>
            )}
            {!hasPermissionToCreateNotation && !isLoading && (
              <ErrorBox
                minHeight={200}
                alignContent="center"
                alignItems="center"
                justifyContent="center"
                display="flex"
                message="You don't have permission to access this tool. Please contact your Beacon administrator on campus."
              />
            )}
            {hasPermissionToCreateNotation && !isLoading && !isErrored && (
              <>
                <SubmitConfirmation />
                {!selectedNotationType && (
                  <MainNotationSelection
                    isPreview={props.isPreview}
                    setStage={setSelectedNotationType}
                    showEncouragement={
                      encouragementPermissions.encouragementShowPermission
                    }
                    showAlert={alertPermissions.alertShowPermission}
                    showUpdate={updatePermissions.updateShowPermission}
                  />
                )}
                {alertPermissions.alertShowPermission && (
                  <Slide
                    in={selectedNotationType === NotationType.Alert}
                    direction="left"
                    mountOnEnter
                    unmountOnExit
                  >
                    <div>
                      <AlertNotationForm
                        reset={isSuccess => {
                          setSelectedNotationType(null);
                          setShowSuccess(isSuccess ?? false);
                        }}
                        isPreview={props.isPreview}
                        {...alertPermissions}
                      />
                    </div>
                  </Slide>
                )}
                {updatePermissions.updateShowPermission && (
                  <Slide
                    in={selectedNotationType === NotationType.Update}
                    direction="left"
                    mountOnEnter
                    unmountOnExit
                  >
                    <div>
                      <UpdateNotationForm
                        reset={isSuccess => {
                          setSelectedNotationType(null);
                          setShowSuccess(isSuccess ?? false);
                        }}
                        isPreview={props.isPreview}
                        {...updatePermissions}
                      />
                    </div>
                  </Slide>
                )}
                {encouragementPermissions.encouragementShowPermission && (
                  <Slide
                    in={selectedNotationType === NotationType.Encouragement}
                    direction="left"
                    mountOnEnter
                    unmountOnExit
                  >
                    <div>
                      <EncouragementNotationForm
                        reset={isSuccess => {
                          setSelectedNotationType(null);
                          setShowSuccess(isSuccess ?? false);
                        }}
                        isPreview={props.isPreview}
                        {...encouragementPermissions}
                      />
                    </div>
                  </Slide>
                )}
              </>
            )}
          </Box>
        </Paper>
      </ThemeProvider>
    </StyledEngineProvider>
  );
};

export default NotationWidgetSelection;
